import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";

const PRIORITY_LAST_NAMES = ["kraehn", "repusseau"];

function profileScore(f) {
    let s = 0;
    if (f.photo_url) s += 3;
    if (f.short_bio && f.short_bio.length > 60) s += 3;
    if ((f.specialties?.length || 0) > 0) s += 2;
    if (((f.sports_associations?.length || 0) + (f.professional_associations?.length || 0)) > 0) s += 2;
    if ((f.qualifications?.length || 0) > 0) s += 1;
    if ((f.years_experience || 0) > 0) s += 1;
    return s;
}

function initials(name) {
    return (name || "?")
        .split(" ")
        .filter(Boolean)
        .slice(0, 2)
        .map((w) => w[0]?.toUpperCase())
        .join("");
}

function FormateurCard({ f }) {
    const hasPhoto = !!f.photo_url;
    const tags = (f.specialties || []).slice(0, 2);
    return (
        <Link
            to="/nos-formateurs"
            data-testid={`marquee-formateur-${f.id}`}
            className="group relative flex w-72 shrink-0 flex-col overflow-hidden rounded-3xl border border-brand-line bg-white shadow-premium transition-all duration-500 hover:-translate-y-1.5 hover:border-brand-orange/30 hover:shadow-[0_30px_60px_rgba(232,135,60,0.15)]"
        >
            <div className="relative aspect-[4/5] w-full overflow-hidden bg-brand-sand/40">
                {hasPhoto ? (
                    <img
                        src={f.photo_url}
                        alt={f.full_name}
                        loading="lazy"
                        className="h-full w-full object-cover transition-transform duration-[1200ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-[1.04]"
                    />
                ) : (
                    <div className="flex h-full w-full items-center justify-center bg-brand-orange/10 font-display text-6xl text-brand-orange">
                        {initials(f.full_name)}
                    </div>
                )}
                <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-black/35 to-transparent" />
            </div>
            <div className="flex flex-1 flex-col gap-2 p-5">
                <h3 className="font-display text-lg leading-tight text-brand-ink">
                    {f.full_name}
                </h3>
                {f.professional_title ? (
                    <p className="text-[11px] font-medium uppercase tracking-[0.18em] text-brand-orange line-clamp-1">
                        {f.professional_title}
                    </p>
                ) : null}
                {tags.length > 0 && (
                    <div className="mt-auto flex flex-wrap gap-1.5 pt-2">
                        {tags.map((t) => (
                            <span
                                key={t}
                                className="rounded-full border border-brand-orange/25 bg-brand-orange/5 px-2.5 py-0.5 text-[10px] font-medium text-brand-ink line-clamp-1"
                            >
                                {t}
                            </span>
                        ))}
                    </div>
                )}
            </div>
        </Link>
    );
}

export default function FormateursMarquee() {
    const [formateurs, setFormateurs] = useState([]);

    useEffect(() => {
        let cancelled = false;
        api.get("/public/formateurs")
            .then((res) => {
                if (cancelled) return;
                setFormateurs(Array.isArray(res.data) ? res.data : []);
            })
            .catch(() => {
                if (!cancelled) setFormateurs([]);
            });
        return () => {
            cancelled = true;
        };
    }, []);

    const selected = useMemo(() => {
        if (!formateurs.length) return [];
        // 1. Pin priority profiles first (Kraehn, Repusseau) if present
        const priority = [];
        const rest = [];
        for (const f of formateurs) {
            if (PRIORITY_LAST_NAMES.includes((f.last_name || "").toLowerCase())) {
                priority.push(f);
            } else {
                rest.push(f);
            }
        }
        // 2. Sort rest by completeness score desc
        rest.sort((a, b) => profileScore(b) - profileScore(a));
        // 3. Combine, take 8
        return [...priority, ...rest].slice(0, 8);
    }, [formateurs]);

    if (selected.length === 0) {
        return null;
    }

    // Duplicate for seamless infinite loop
    const loopCards = [...selected, ...selected];

    return (
        <div
            className="relative overflow-hidden"
            data-testid="formateurs-marquee"
            style={{
                maskImage:
                    "linear-gradient(to right, transparent 0%, black 8%, black 92%, transparent 100%)",
                WebkitMaskImage:
                    "linear-gradient(to right, transparent 0%, black 8%, black 92%, transparent 100%)",
            }}
        >
            <div
                className="marquee-track flex w-max gap-6 py-3"
                aria-label="Carrousel des formateurs"
            >
                {loopCards.map((f, idx) => (
                    <FormateurCard key={`${f.id}-${idx}`} f={f} />
                ))}
            </div>
        </div>
    );
}
